import { useAuth } from '@clerk/clerk-react';
import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';

import { useSpaceScope } from '@/contexts/SpaceScopeContext';
import { ApiError, spacesApi } from '@/lib/api';
import { useSpacesEnabled } from '@/lib/spaces/flag';
import type { AnswerSpaceInviteRequest, CreateSpaceRequest, MySpaceInvite, SpaceDetail, SpaceInviteRequest } from '@/types/spaces';
import { useSpaceWrite } from './useSpaceWrite';
import { LONG_LIVED_SWR_OPTIONS, useAuthedFetcher } from './swrConfig';

// Accepting says whether the other members get to see the person's account photo (null = initials only).
export type SpaceInviteAnswer = { answer: 'accept'; photoUrl: string | null } | { answer: 'decline' };

export function useMySpaceInvites() {
  const { userId } = useAuth();
  const enabled = useSpacesEnabled();
  const { scope } = useSpaceScope();
  const fetch = useAuthedFetcher();
  const active = enabled && scope === 'personal' && Boolean(userId);
  const query = useSWR<MySpaceInvite[], Error>(active ? ['space-invites', userId] : null,
    () => fetch(spacesApi.invites), { ...LONG_LIVED_SWR_OPTIONS, keepPreviousData: false, refreshInterval: 60_000 });
  return { ...query, enabled: active };
}

export function useCreateSpace() {
  const { write, remember, userId } = useSpaceWrite();
  return useSWRMutation(['space-create', userId], async (_key, { arg }: { arg: CreateSpaceRequest }) => {
    const space = await write((token) => spacesApi.create(token, arg));
    await remember(space);
    return space;
  });
}

export function useRespondToSpaceInvite(inviteId: string) {
  const { write, remember, mutate, refresh, userId } = useSpaceWrite();
  return useSWRMutation(['space-invite-response', userId, inviteId], async (_key, { arg }: { arg: SpaceInviteAnswer }) => {
    try {
      const body: AnswerSpaceInviteRequest = arg.answer === 'accept'
        ? { status: 'accepted', photoUrl: arg.photoUrl }
        : { status: 'declined' };
      const space = await write((token) => spacesApi.answerInvite(token, inviteId, body));
      if (space) await remember(space);
      await mutate<MySpaceInvite[]>(['space-invites', userId], (current) => current?.filter((invite) => invite.id !== inviteId), { revalidate: false });
      refresh(['space-invites', userId]);
      return space;
    } catch (error) {
      if (error instanceof ApiError && [403, 404, 409].includes(error.status)) refresh(['space-invites', userId]);
      throw error;
    }
  });
}

export function useInviteToSpace(spaceId: string) {
  const { write, mutate, refresh, userId } = useSpaceWrite();
  return useSWRMutation(['space-invite-create', userId, spaceId], async (_key, { arg }: { arg: SpaceInviteRequest }) => {
    const invite = await write((token) => spacesApi.invite(token, spaceId, arg));
    refresh(['space', userId, spaceId]);
    refreshSpaceAudit(mutate, userId, spaceId);
    return invite;
  });
}

export function useRevokeSpaceInvite(spaceId: string, inviteId: string) {
  const { write, mutate, refresh, userId } = useSpaceWrite();
  return useSWRMutation(['space-invite-revoke', userId, spaceId, inviteId], async () => {
    await write((token) => spacesApi.revokeInvite(token, spaceId, inviteId));
    await mutate<SpaceDetail>(['space', userId, spaceId], (current) => current
      ? { ...current, pendingInvites: current.pendingInvites.filter((invite) => invite.id !== inviteId) } : current, { revalidate: false });
    refresh(['space', userId, spaceId]);
    refreshSpaceAudit(mutate, userId, spaceId);
  });
}

// Inviting and revoking are audited; the history pages of this space are keyed by offset, hence the filter.
function refreshSpaceAudit(mutate: ReturnType<typeof useSpaceWrite>['mutate'], userId: string | null | undefined, spaceId: string): void {
  void mutate((key) => Array.isArray(key) && key[0] === 'space-audit' && key[1] === userId && key[2] === spaceId).catch(() => undefined);
}
